import React, { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { KeyIcon } from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { COUNTRY_CODES, CountryCode } from "@component/country-codes-data";
import { authGoogleConnect, updateCandidateProfile, updateEmployeerProfile } from "@service/auth";
import { useUserProfileContext } from "@context/user_profile";
import { useGoogleLogin } from "@react-oauth/google";
import PasswordManagementDialog from "@container/password-management-dialog";
import SettingsTwoPanelLayout from "@component/setting-two-panel";
import { uploadProfilePicture } from "@service/cv";
import { uploadCompanyProfile } from "@service/employer";
import CountryCodeSelect from "@component/country-code-select";
import GoogleSSOPasswordDialog from "./google-sso-password-dialog";
import googleIcon from '@asset/google-icon.png';
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import { useOuraMerchantButton } from "@oura/role";

export default function SettingProfileFormV2() {
    // Oura merchants get pill buttons; every other product keeps the shared shape.
    const pill = useOuraMerchantButton();
    const snackbar = useSnackbar();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const initialFormData = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        country: "+1",
    };
    const [formData, setFormData] = useState(initialFormData);
    const [isLoadingSetting, setLoadingSetting] = useState<boolean>(false);
    const [isLoadingGoogle, setIsLoadingGoogle] = useState<boolean>(false);
    const [avatar, setAvatar] = useState<string | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>({
        code: "+1",
        name: "United States",
        flag: "🇺🇸",
    });
    const [isGoogleSSOActive, setIsGoogleSSOActive] = React.useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [googleSSOPasswordDialogOpen, setGoogleSSOPasswordDialogOpen] =
        useState(false);

    // Authentication method state - in a real app, this would come from your auth context/state
    const [authenticationMethod] = useState<
        "google" | "email"
    >("email");

    useEffect(() => {
        if (!isLoadingUserProfile && userInfo) {
            setFormData({
                firstName: userInfo?.first_name || "",
                lastName: userInfo?.last_name || "",
                email: userInfo?.email || "",
                phone: userInfo?.phone_number || "",
                country: userInfo?.country_code || "+1",
            });

            setSelectedCountry(COUNTRY_CODES.find(
                (country) => country.code === userInfo?.country_code
            ) || {
                code: "+1",
                name: "United States",
                flag: "🇺🇸",
            });

            setIsGoogleSSOActive(userInfo?.is_connected_google || false);
            if (userInfo?.role !== "job_seeker") {
                setAvatar(userInfo?.company_logo || null);
            } else {
                setAvatar(userInfo?.profile_picture_path || null);
            }
        }
    }, [isLoadingUserProfile]);

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Check if form has changes
    const hasChanges = React.useMemo(() => {
        return formData.firstName !== userInfo?.first_name || formData.lastName !== userInfo?.last_name || (userInfo.role === "job_seeker" && formData.phone !== userInfo?.phone_number) || userInfo?.country_code !== formData.country;
    }, [formData]);

    const handleSave = async () => {
        setLoadingSetting(true)
        try {
            if (userInfo?.role === "job_seeker") {
                await updateCandidateProfile({
                    username: userInfo?.username || "",
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    country_code: formData.country,
                    phone_number: formData.phone,
                })
            } else {
                await updateEmployeerProfile({
                    username: userInfo?.username || "",
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    company_name: userInfo?.company_name || "",
                    company_linkedin: userInfo?.company_linkedin || "",
                })
            }

            snackbar.showSuccess("Your personal information has been saved successfully.");
        } catch (error) {
            snackbar.showError(
                "Unable to save personal information. Please check your connection and try again."
            );
            catchErrorLog(`Failed to update profile: ${error}`);
        } finally {
            setLoadingSetting(false);
        }
    };

    const connectGoogleSSO = useGoogleLogin({
        scope: 'email profile',
        onSuccess: async (tokenResponse) => {
            try {
                setIsLoadingGoogle(true);
                // First, fetch user profile information from Google
                const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                    headers: {
                        Authorization: `Bearer ${tokenResponse.access_token}`,
                    },
                });
                if (!userInfoResponse.ok) {
                    throw new Error(`Failed to fetch user info: ${userInfoResponse.statusText}`);
                }

                const userGoogleInfo = await userInfoResponse.json();

                if (userGoogleInfo.email !== userInfo?.email) {
                    snackbar.showError(
                        "The email from Google does not match your account email. Please use the same email to connect."
                    );
                    setIsLoadingGoogle(false);
                    return;
                }

                const authGoogleSSOResp = await authGoogleConnect(userGoogleInfo.sub)
                if (authGoogleSSOResp.status === 200) {
                    setIsGoogleSSOActive(true);
                } else {
                    snackbar.showError(
                        authGoogleSSOResp.message || "Failed to connect Google SSO."
                    );
                }

                setIsLoadingGoogle(false);
            } catch (error) {
                const errorMessage = error && typeof error === 'object' && 'response' in error
                    ? (error.response as any)?.data?.message
                    : error instanceof Error
                        ? error.message
                        : "An unexpected error occurred. Please try again.";

                snackbar.showError(
                    errorMessage
                );
                console.error("Error during Google signup:", error);
                setIsLoadingGoogle(false);
            }
        },
        onError: () => {
            snackbar.showError(
                "Failed to connect with Google. Please try again."
            );
            setIsLoadingGoogle(false);
        }
    });

    const handleOpenPasswordDialog = () => {
        setPasswordDialogOpen(true);
    };

    const handleFileChange = async (file: File) => {
        try {
            // Create a preview with FileReader
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === "string") {
                    setAvatar(reader.result as string);
                }
            };
            reader.readAsDataURL(file);
            if (userInfo?.role === "job_seeker") {
                const response = await uploadProfilePicture(file);
                if (response.success && response.data) {
                    setAvatar(response.data?.profile_picture_path || null);
                } else {
                    throw new Error(response.message || "Failed to upload profile picture.");
                }
            } else {
                const response = await uploadCompanyProfile(file);
                if (response.success && response.data) {
                    // Update the avatar with the uploaded URL
                    setAvatar(response.data)
                } else {
                    throw new Error(response.message || "Failed to upload profile picture.");
                }
            }
        } catch (error) {
            console.error(
                "Failed to upload profile picture:",
                error instanceof Error ? error.message : "Unknown error"
            );
            snackbar.showError(
                "Profile Picture Upload Error",
                error instanceof Error ? error.message : "An unexpected error occurred."
            );
        }
    };

    return (
        <div className="space-y-8">
            {/* Profile Details Section */}
            <SettingsTwoPanelLayout
                title="Profile"
                description="Your personal information and contact details."
                showSaveButton={true}
                saveButtonClassName={pill}
                onSave={handleSave}
                isLoading={isLoadingSetting}
                hasChanges={hasChanges}
            >
                {/* Avatar Section */}
                <div className="space-y-6">
                    <div className="space-y-2" style={{ paddingBottom: "16px" }}>
                        <Label
                            className="text-sm font-medium text-gray-700"
                            style={{ color: "#000000" }}
                        >
                            Profile Photo
                        </Label>
                        <div className="flex items-center space-x-4">
                            <div className="h-16 w-16 overflow-hidden flex items-center justify-center rounded-full border border-gray-300">
                                <img
                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${avatar}`}
                                    onError={(e) => {
                                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/${userInfo?.role === "admin" || userInfo?.role === "super_admin" ? "super-admin-logo.png" : "default_avatar.png"}`;
                                    }}
                                />
                            </div>
                            <div className="relative space-y-1">
                                <Button variant="outline" className={cn("flex items-center cursor-pointer", pill)}>
                                    Change avatar
                                </Button>
                                <input
                                    type="file"
                                    accept="image/*"
                                    disabled={userInfo?.role === "admin" || userInfo?.role === "super_admin"}
                                    className="absolute inset-0 opacity-0 cursor-pointer w-[120px] top-0"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleFileChange(e.target.files[0]);
                                        }
                                    }}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Recommended: square image (e.g. 50x50 pixels)
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            style={{ paddingBottom: "16px" }}
                        >
                            <div className="space-y-2">
                                <Label
                                    htmlFor="firstName"
                                    className="text-sm font-medium text-gray-700"
                                    style={{ color: "#000000" }}
                                >
                                    First Name
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="firstName"
                                        value={formData.firstName}
                                        onChange={(e) =>
                                            handleInputChange("firstName", e.target.value)
                                        }
                                        className="pl-10"
                                    />

                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                            className="h-4 w-4 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label
                                    htmlFor="lastName"
                                    className="text-sm font-medium text-gray-700"
                                    style={{ color: "#000000" }}
                                >
                                    Last Name
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="lastName"
                                        value={formData.lastName}
                                        onChange={(e) =>
                                            handleInputChange("lastName", e.target.value)
                                        }
                                        className="pl-10"
                                    />

                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                            className="h-4 w-4 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            style={{ paddingBottom: "16px" }}
                        >
                            {userInfo?.role === "job_seeker" && (
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                                        Phone
                                    </Label>
                                    <div className="flex">
                                        <CountryCodeSelect
                                            value={selectedCountry}
                                            onChange={(value) => {
                                                setSelectedCountry(value);
                                                handleInputChange("country", value.code);
                                            }}
                                        />
                                        <Input
                                            id="phone"
                                            type="tel"
                                            maxLength={18}
                                            value={formData.phone}
                                            className="rounded-l-none border-l-0"
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                handleInputChange("phone", value);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label
                                    htmlFor="email"
                                    className="text-sm font-medium text-gray-700"
                                    style={{ color: "#000000" }}
                                >
                                    Email
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        disabled
                                        className="pl-10"
                                        title="Ask admin to change the email"
                                    />

                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                            className="h-4 w-4 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </SettingsTwoPanelLayout>

            {/* Divider */}
            <hr className="border-gray-200" />

            {/* Authentication Method Section */}
            <SettingsTwoPanelLayout
                title="Authentication Method"
                description="Manage how you sign in to your account."
            >
                <div className="space-y-4">
                    {/* Google SSO Method */}
                    <div
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg pr-8"
                        style={{ paddingTop: "16px", paddingBottom: "16px" }}
                    >
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <img src={googleIcon} className="w-7 h-7" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-gray-900 text-sm">
                                    Google Single Sign-On
                                </h4>
                                <p className="text-xs text-blue-600">
                                    Connect your Google account for easier sign-in.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => connectGoogleSSO()}
                            disabled={isGoogleSSOActive || isLoadingGoogle}
                            className="text-blue-600 hover:underline text-sm font-medium disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                        >
                            {isGoogleSSOActive ? "Connected" : "Connect"}
                        </button>
                    </div>

                    {/* Email + Password Method */}
                    <div
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg pr-8"
                        style={{ paddingTop: "16px", paddingBottom: "16px" }}
                    >
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                                    <svg
                                        className="h-5 w-5 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-gray-900 text-sm">
                                    Email & Password
                                </h4>
                                <p className="text-xs text-blue-600">
                                    You're signed in with your email and password.
                                </p>
                            </div>
                        </div>
                        <button
                            // onClick={() => handleAuthenticationMethodChange("email")}
                            disabled={authenticationMethod === "email"}
                            className="text-blue-600 hover:underline text-sm font-medium disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                        >
                            {authenticationMethod === "email" ? "Connected" : "Connect"}
                        </button>
                    </div>
                </div>
            </SettingsTwoPanelLayout>

            {/* Divider */}
            <hr className="border-gray-200" />

            {/* Password Management Section */}
            <SettingsTwoPanelLayout
                title="Password Management"
                description="Manage your account password to keep your account secure."
            >
                <div className="flex justify-start">
                    {isGoogleSSOActive ? (
                        <Button
                            disabled
                            variant="outline"
                            className={cn("flex items-center space-x-2 opacity-50 cursor-not-allowed", pill)}
                        >
                            <KeyIcon className="h-4 w-4" />

                            <span>Change Password</span>
                        </Button>
                    ) : (
                        <Button
                            onClick={handleOpenPasswordDialog}
                            variant="outline"
                            className={cn("flex items-center space-x-2", pill)}
                        >
                            <KeyIcon className="h-4 w-4" />

                            <span>Change Password</span>
                        </Button>
                    )}
                </div>
            </SettingsTwoPanelLayout>

            <PasswordManagementDialog
                open={passwordDialogOpen}
                onOpenChange={setPasswordDialogOpen}
            />

            <GoogleSSOPasswordDialog
                open={googleSSOPasswordDialogOpen}
                onOpenChange={setGoogleSSOPasswordDialogOpen}
            />
        </div>
    );
}
